import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import type { GameResult } from '../../../hooks/useGameState'
import { getImageUrl } from '../../../lib/api'

// Confetti effect function (same as DailyWord)
const triggerConfetti = () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return
  }

  const canvas = document.createElement('canvas')
  canvas.style.position = 'fixed'
  canvas.style.top = '0'
  canvas.style.left = '0'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.pointerEvents = 'none'
  canvas.style.zIndex = '9999'
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = window.innerWidth
  canvas.height = window.innerHeight

  const particles: Array<{
    x: number
    y: number
    vx: number
    vy: number
    color: string
    size: number
  }> = []

  const colors = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444']

  for (let i = 0; i < 50; i++) {
    particles.push({
      x: canvas.width / 2,
      y: canvas.height / 2,
      vx: (Math.random() - 0.5) * 10,
      vy: (Math.random() - 0.5) * 10 - 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 5 + 3,
    })
  }

  let animationId: number
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    particles.forEach((particle, index) => {
      particle.x += particle.vx
      particle.y += particle.vy
      particle.vy += 0.3

      ctx.fillStyle = particle.color
      ctx.beginPath()
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
      ctx.fill()

      if (
        particle.y > canvas.height ||
        particle.x < 0 ||
        particle.x > canvas.width
      ) {
        particles.splice(index, 1)
      }
    })

    if (particles.length > 0) {
      animationId = requestAnimationFrame(animate)
    } else {
      document.body.removeChild(canvas)
    }
  }

  animate()

  setTimeout(() => {
    if (canvas.parentNode) {
      document.body.removeChild(canvas)
    }
    if (animationId) {
      cancelAnimationFrame(animationId)
    }
  }, 3000)
}

interface PicRevealProps {
  imageUrl?: string
  imageId?: number
  answer: string
  aliases: string[]
  gameState?: GameResult
  onComplete: (result: GameResult) => void
}

const MAX_ATTEMPTS = 5
const BLUR_INCREMENT = 25 // Reduce blur by 25% per wrong guess

export default function PicReveal({ imageUrl, imageId, answer, aliases, gameState, onComplete }: PicRevealProps) {
  // Use provided URL or construct from imageId
  const finalImageUrl = imageUrl || (imageId ? getImageUrl(imageId) : '')
  const [guess, setGuess] = useState('')
  const [attempts, setAttempts] = useState(gameState?.attempts || 0)
  const [isComplete, setIsComplete] = useState(gameState?.status === 'won' || gameState?.status === 'lost')
  const [blurLevel, setBlurLevel] = useState(isComplete ? 0 : 100)
  const [previousGuesses, setPreviousGuesses] = useState<string[]>([])

  const normalizedAnswer = answer.toLowerCase().trim()
  const normalizedAliases = aliases.map(a => a.toLowerCase().trim())

  useEffect(() => {
    if (gameState?.status === 'won') {
      setBlurLevel(0)
      setIsComplete(true)
    } else if (gameState?.status === 'lost') {
      setIsComplete(true)
      // Calculate blur level based on attempts
      const calculatedBlur = Math.max(0, 100 - (attempts * BLUR_INCREMENT))
      setBlurLevel(calculatedBlur)
    }
  }, [gameState, attempts])

  const checkGuess = (userGuess: string): boolean => {
    const normalized = userGuess.toLowerCase().trim()
    
    // Check exact match
    if (normalized === normalizedAnswer) return true
    
    // Check aliases
    return normalizedAliases.some(alias => {
      // Fuzzy match - allow partial matches
      return alias.includes(normalized) || normalized.includes(alias)
    })
  }

  const handleSubmit = () => {
    if (!guess.trim() || isComplete) return

    const newAttempts = attempts + 1
    setAttempts(newAttempts)
    setPreviousGuesses(prev => [...prev, guess])

    if (checkGuess(guess)) {
      setIsComplete(true)
      setBlurLevel(0)
      // Trigger confetti on win
      triggerConfetti()
      onComplete({
        status: 'won',
        attempts: newAttempts
      })
    } else if (newAttempts >= MAX_ATTEMPTS) {
      setIsComplete(true)
      setBlurLevel(0)
      onComplete({
        status: 'lost',
        attempts: newAttempts
      })
    } else {
      // Reduce blur
      const newBlur = Math.max(0, 100 - (newAttempts * BLUR_INCREMENT))
      setBlurLevel(newBlur)
    }

    setGuess('')
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Pic Reveal</h2>
        {isComplete && gameState?.status === 'won' && (
          <span className="text-green-600 font-semibold">✅ Solved in {gameState.attempts} attempts</span>
        )}
        {isComplete && gameState?.status === 'lost' && (
          <span className="text-red-600 font-semibold">❌ Answer: {answer}</span>
        )}
      </div>

      {/* Image Container */}
      <div className="relative mb-6 rounded-lg overflow-hidden bg-gray-100 min-h-[300px] flex items-center justify-center">
        {finalImageUrl && !finalImageUrl.includes('example.com') ? (
          <motion.img
            src={finalImageUrl}
            alt="Puzzle image"
            className="w-full h-auto max-h-96 object-cover"
            style={{
              filter: `blur(${blurLevel}px)`,
              transition: 'filter 0.3s ease-in-out'
            }}
            animate={isComplete && gameState?.status === 'won' ? { scale: [1, 1.02, 1] } : {}}
            transition={{ duration: 0.5 }}
            onError={(e) => {
              // Fallback if image fails to load
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        ) : (
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🖼️</div>
            <p className="text-gray-600 mb-2">Image placeholder</p>
            <p className="text-sm text-gray-500">
              For testing: Try guessing "dosa" or "dosai"
            </p>
          </div>
        )}
        {!isComplete && finalImageUrl && !finalImageUrl.includes('example.com') && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
              <p className="text-sm">Blur: {blurLevel}%</p>
              <p className="text-xs mt-1">Attempts: {attempts}/{MAX_ATTEMPTS}</p>
            </div>
          </div>
        )}
      </div>

      {/* Guess Input */}
      {!isComplete && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isComplete && guess.trim()) {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
              placeholder="What is this?"
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isComplete}
              autoFocus={!isComplete}
            />
            <button
              onClick={handleSubmit}
              disabled={!guess.trim() || isComplete}
              className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guess
            </button>
          </div>

          {/* Previous Guesses */}
          {previousGuesses.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 font-semibold">Previous guesses:</p>
              <div className="flex flex-wrap gap-2">
                {previousGuesses.map((prevGuess, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {prevGuess}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(attempts / MAX_ATTEMPTS) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600">
              {MAX_ATTEMPTS - attempts} attempts left
            </span>
          </div>
        </div>
      )}

      {isComplete && gameState?.status === 'won' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 text-center text-green-600 font-semibold"
        >
          🎉 Great job! You guessed it!
        </motion.div>
      )}
    </div>
  )
}
